import React, { useState, useRef } from 'react';
import { DebugSheet } from './debug';
import { Icon } from './icons';
import { Flame, Toggle } from './common';
import { ProfileV2 } from '../types';
import { PLAN_NAMES, Plan, acessoSemAssinatura, rotuloDoAcesso } from './plans';

/* ─────────────────────────────────────────────────────────────
   NotifScreen — central completa (toggle + horário + frequência)
   ───────────────────────────────────────────────────────────── */

export interface NotifConfig {
  zeroReading:  { enabled: boolean; startTime: string; endTime: string; frequencyValue: number; frequencyUnit: 'minutes' | 'hours' };
  belowGoal:    { enabled: boolean; startTime: string; endTime: string; frequencyValue: number; frequencyUnit: 'minutes' | 'hours' };
  bookGoalHit:  { enabled: boolean };
  dailyGoalHit: { enabled: boolean };
}

// Os MESMOS padrões do servidor (server/notificationConfig.js). Antes a tela
// tinha os seus — a cada 30 min das 9h às 23h — e mostrava isso a quem nunca
// mexeu, enquanto o servidor fazia outra coisa (pente fino, UX-12/QA-05).
const DEFAULT_NOTIF_CONFIG: NotifConfig = {
  zeroReading:  { enabled: true, startTime: '19:00', endTime: '21:00', frequencyValue: 24, frequencyUnit: 'hours' },
  belowGoal:    { enabled: true, startTime: '19:00', endTime: '21:00', frequencyValue: 24, frequencyUnit: 'hours' },
  bookGoalHit:  { enabled: true },
  dailyGoalHit: { enabled: true },
};

export { DEFAULT_NOTIF_CONFIG };

export interface PushStatus {
  supported: boolean;
  permission: 'default' | 'granted' | 'denied';
  subscribed: boolean;
  busy: boolean;
  error: string | null;
  isIOS: boolean;
  isStandalone: boolean;
}

export const NotifScreen: React.FC<{
  onBack: () => void;
  config: NotifConfig;
  onChange: (next: NotifConfig) => void;
  pushStatus?: PushStatus;
  onActivatePush?: () => void | Promise<void>;
  onTestPush?: () => void | Promise<void>;
  onRefreshPushStatus?: () => void | Promise<void>;
  isAdmin?: boolean;
}> = ({ onBack, config, onChange, pushStatus, onActivatePush, onTestPush, onRefreshPushStatus, isAdmin }) => {
  const estado: EstadoPush = pushStatus ? estadoDoPush(pushStatus) : 'active';
  const recebendo = estado === 'active';
  // Interruptor tocado sem o aparelho receber: o passo seguinte é ativar.
  const pedirAtivacao = () => { if (estado === 'needs-activate' && onActivatePush) onActivatePush(); };
  const update = <K extends keyof NotifConfig>(key: K, patch: Partial<NotifConfig[K]>) => {
    onChange({ ...config, [key]: { ...(config[key] as any), ...patch } });
  };

  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      <div style={{ padding: '40px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--line)',
        }}>
          <Icon name="chevron-left" size={18} />
        </button>
        <div style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Hábito</div>
      </div>

      <div style={{ padding: '16px 20px 0' }}>
        <div className="serif" style={{ fontSize: 32, lineHeight: 1.05, letterSpacing: '-0.01em' }}>
          Quando o app<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>te chama</span>.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, maxWidth: 300 }}>
          No máximo um aviso por dia, entre 8h e 21h30 — e nenhum lembrete no dia em que você já leu. Você escolhe quais avisos quer e em que horário.
        </div>
      </div>

      {pushStatus && (
        <div style={{ padding: '24px 20px 0' }}>
          <PushStatusCard
            status={pushStatus}
            onActivate={onActivatePush}
            onTest={onTestPush}
            onRefresh={onRefreshPushStatus}
          />
          <DiagnosticCard onActivatePush={onActivatePush} isAdmin={isAdmin} />
        </div>
      )}

      <div style={{ padding: '24px 20px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* OS INTERRUPTORES DIZEM A VERDADE (pente fino, UX-21). Com as
            notificações inativas neste aparelho, eles apareciam LIGADOS — e o
            leitor achava que ia receber aviso. Agora ficam desligados e
            "aguardando", com o caminho para ativar logo aqui. A escolha da
            pessoa fica guardada: vale assim que o aparelho passar a receber. */}
        {!recebendo && (
          <div role="status" style={{
            padding: '14px 16px', borderRadius: 16,
            background: 'rgba(199,83,26,0.08)', border: '1px solid rgba(199,83,26,0.35)',
          }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 600 }}>
              Ative as notificações para receber estes avisos.
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.45 }}>
              {estado === 'denied'
                ? 'As notificações deste app estão bloqueadas no aparelho. Permita nas configurações do navegador ou do celular e volte aqui.'
                : estado === 'ios-install'
                  ? 'No iPhone, primeiro adicione o app à Tela de Início e abra por lá.'
                  : estado === 'unsupported'
                    ? 'Este navegador não recebe notificações. Use o app ou um navegador atualizado.'
                    : 'Por enquanto nenhum aviso chega a este aparelho. As suas escolhas abaixo ficam guardadas.'}
            </div>
            {estado === 'needs-activate' && onActivatePush && (
              <button onClick={() => onActivatePush()} disabled={pushStatus?.busy} style={{
                marginTop: 12, width: '100%', padding: '12px 16px', borderRadius: 12,
                background: 'var(--ember)', color: 'var(--paper)', fontSize: 14, fontWeight: 600,
              }}>{pushStatus?.busy ? 'Ativando...' : 'Ativar notificações'}</button>
            )}
          </div>
        )}

        <RecurringCard
          icon="sun"
          title="Quando não ler"
          desc="Um lembrete gentil, uma vez, se o dia passar sem leitura."
          cfg={config.zeroReading}
          onUpdate={patch => update('zeroReading', patch)}
          recebendo={recebendo}
          onPedirAtivacao={pedirAtivacao}
        />

        {/* "Abaixo da meta" saiu da tela: pela regra do push (server/push/
            politica.js) quem já leu no dia não recebe lembrete — e quem está
            abaixo da meta já leu. O cartão ligava algo que nunca chega. */}

        <SimpleCard
          icon="flame"
          title="Meta do livro batida"
          desc="Celebrar quando você cumpre a meta diária de um livro específico."
          enabled={config.bookGoalHit.enabled}
          onToggle={v => update('bookGoalHit', { enabled: v })}
          recebendo={recebendo}
          onPedirAtivacao={pedirAtivacao}
        />

        <SimpleCard
          icon="star"
          title="Meta do dia batida"
          desc="Quando a soma das leituras do dia bate a meta total."
          enabled={config.dailyGoalHit.enabled}
          onToggle={v => update('dailyGoalHit', { enabled: v })}
          recebendo={recebendo}
          onPedirAtivacao={pedirAtivacao}
        />
      </div>

      <div style={{ padding: '28px 20px 0' }}>
        <div style={{
          padding: 18, borderRadius: 18,
          background: 'var(--paper-2)', border: '1px dashed var(--line-strong)',
          display: 'flex', gap: 12,
        }}>
          <div style={{ flexShrink: 0 }}><Icon name="sparkle" size={22} color="var(--ember)"/></div>
          <div>
            <div className="serif" style={{ fontSize: 17, lineHeight: 1.15 }}>Poucos e úteis</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4 }}>
              Somando todos os tipos, são no máximo um aviso por dia e três por semana. O lembrete fala do seu livro e da página em que você parou.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────── Card de status do push ─────── */

/** Em que pé está o push NESTE aparelho. Mesma regra do cartão de status. */
type EstadoPush = 'active' | 'needs-activate' | 'denied' | 'ios-install' | 'unsupported';
function estadoDoPush(st: PushStatus): EstadoPush {
  if (!st.supported) return 'unsupported';
  if (st.isIOS && !st.isStandalone) return 'ios-install';
  if (st.permission === 'denied') return 'denied';
  if (st.subscribed && st.permission === 'granted') return 'active';
  return 'needs-activate';
}

function PushStatusCard({ status, onActivate, onTest, onRefresh }: {
  status: PushStatus;
  onActivate?: () => void | Promise<void>;
  onTest?: () => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
}) {
  const { supported, permission, subscribed, busy, error, isIOS, isStandalone } = status;

  const state: EstadoPush = estadoDoPush(status);
  void supported; void permission; void subscribed; void isIOS; void isStandalone;

  const colors: Record<typeof state, { bg: string; ring: string; icon: string; iconColor: string; label: string }> = {
    active:          { bg: 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))', ring: 'var(--ember)',      icon: 'bell',  iconColor: 'var(--ember)', label: 'Ativadas' },
    'needs-activate':{ bg: 'var(--paper-2)',                                              ring: 'var(--line-strong)',icon: 'bell',  iconColor: 'var(--ink-3)', label: 'Inativas' },
    denied:          { bg: 'rgba(199,83,26,0.06)',                                        ring: 'rgba(199,83,26,0.3)',icon: 'lock',iconColor: 'var(--ember)', label: 'Bloqueadas' },
    'ios-install':   { bg: 'var(--paper-2)',                                              ring: 'var(--line-strong)',icon: 'phone', iconColor: 'var(--ink-3)', label: 'Só no app' },
    unsupported:     { bg: 'var(--paper-2)',                                              ring: 'var(--line)',       icon: 'close', iconColor: 'var(--ink-3)', label: 'Sem suporte' },
  };
  const c = colors[state];

  const messages: Record<typeof state, string> = {
    active:          'Seu celular está recebendo notificações deste app.',
    'needs-activate':'Permita o envio de notificações para receber os lembretes configurados abaixo.',
    denied:          'Você bloqueou este site. Abra as configurações do navegador, permita notificações para este app e recarregue a página.',
    // QA-27: a venda e o push no iPhone são pelo app da App Store, não pelo
    // site salvo na Tela de Início.
    'ios-install':   'Para receber os avisos no iPhone, baixe o app Forjamente na App Store e entre com a mesma conta.',
    unsupported:     'Este navegador não suporta notificações push. Use Chrome, Firefox, Edge ou Safari atualizado.',
  };

  return (
    <div style={{
      padding: 16, borderRadius: 18,
      background: c.bg, border: `1px solid ${c.ring}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: state === 'active' ? 'var(--ember)' : 'var(--paper-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          boxShadow: state === 'active' ? '0 6px 18px -4px rgba(199,83,26,0.45)' : 'none',
        }}>
          <Icon name={c.icon} size={20} color={state === 'active' ? 'var(--paper)' : c.iconColor} stroke={2}/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="serif" style={{ fontSize: 19, lineHeight: 1.1 }}>
              Notificações <span style={{ color: c.iconColor, fontStyle: 'italic' }}>{c.label.toLowerCase()}</span>
            </div>
            <span className="mono" style={{
              fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: state === 'active' ? 'var(--ember)' : 'var(--ink-3)',
              padding: '3px 8px', borderRadius: 999,
              background: state === 'active' ? 'var(--ember-soft)' : 'var(--paper-3)',
            }}>
              {state === 'active' ? '● permitido' : state === 'denied' ? '● bloqueado' : '○ inativo'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.4 }}>
            {messages[state]}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {state === 'ios-install' && (
          <a href="/baixar" data-baixar-app style={{
            flex: 1, minWidth: 140,
            padding: '12px 16px', borderRadius: 12,
            background: 'var(--ember)', color: 'var(--paper)',
            fontSize: 14, fontWeight: 500, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="phone" size={14} color="var(--paper)" stroke={2.2}/>
            Baixe o app
          </a>
        )}
        {state === 'needs-activate' && (
          <button onClick={() => onActivate?.()} disabled={busy} style={{
            flex: 1, minWidth: 140,
            padding: '12px 16px', borderRadius: 12,
            background: 'var(--ember)', color: 'var(--paper)',
            fontSize: 14, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="bell" size={14} color="var(--paper)" stroke={2.2}/>
            {busy ? 'Ativando...' : 'Ativar notificações'}
          </button>
        )}
        {state === 'active' && onTest && (
          <button onClick={() => onTest()} disabled={busy} style={{
            flex: 1, minWidth: 140,
            padding: '12px 16px', borderRadius: 12,
            background: 'var(--ink)', color: 'var(--paper)',
            fontSize: 13, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="sparkle" size={13} color="var(--paper)" stroke={2.2}/>
            {busy ? 'Enviando...' : 'Testar notificação'}
          </button>
        )}
        {onRefresh && (
          <button onClick={() => onRefresh()} disabled={busy} style={{
            padding: '12px 14px', borderRadius: 12,
            background: 'var(--paper-3)', color: 'var(--ink-2)',
            fontSize: 13, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Icon name="sparkle" size={13} color="var(--ink-2)" stroke={2}/>
            Atualizar
          </button>
        )}
      </div>

      {/* Mensagem de erro: SEMPRE depois dos botões — assim o user já vê o
          botão de "Atualizar" pra tentar de novo logo abaixo do erro.
          Persiste até o user clicar Atualizar (gerenciado em App.tsx). */}
      {error && (
        <div style={{
          marginTop: 12, padding: '12px 14px', borderRadius: 12,
          background: 'rgba(199,83,26,0.12)',
          border: '1px solid rgba(199,83,26,0.35)',
          color: 'var(--ember)',
          fontSize: 12.5, lineHeight: 1.5,
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <Icon name="close" size={14} color="var(--ember)" stroke={2.2}/>
          <div style={{ flex: 1, fontWeight: 500 }}>{error}</div>
        </div>
      )}
    </div>
  );
}

/* ─────── Cards internos ─────── */

function RecurringCard({ icon, title, desc, cfg, onUpdate, recebendo = true, onPedirAtivacao }: {
  icon: string; title: string; desc: string;
  cfg: NotifConfig['zeroReading'];
  onUpdate: (patch: Partial<NotifConfig['zeroReading']>) => void;
  recebendo?: boolean;
  onPedirAtivacao?: () => void;
}) {
  const ligado = cfg.enabled && recebendo;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: ligado ? 'var(--ember-soft)' : 'var(--paper-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={icon} size={20} color={ligado ? 'var(--ember)' : 'var(--ink-3)'}/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="serif" style={{ fontSize: 19, lineHeight: 1.1 }}>{title}</div>
            <Toggle on={ligado} onChange={v => (recebendo ? onUpdate({ enabled: v }) : onPedirAtivacao && onPedirAtivacao())}/>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{desc}</div>
          {!recebendo && <AguardandoAtivacao />}
        </div>
      </div>

      {/* O horário segue ajustável enquanto aguarda a ativação (esmaecido):
          a pessoa deixa tudo pronto e só falta ligar o aparelho. */}
      {cfg.enabled && (
        <div style={{
          marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
          opacity: recebendo ? 1 : 0.6,
        }}>
          <TimeField label="De" value={cfg.startTime} onChange={v => onUpdate({ startTime: v })}/>
          <TimeField label="Até" value={cfg.endTime} onChange={v => onUpdate({ endTime: v })}/>
          {/* "Avisar a cada" saiu: o teto é de um por dia, então a frequência
              não muda nada — e a lista, que só ia até 12 h, mostrava "1 min"
              para todo mundo (o padrão do servidor é 24 h). */}
          <div style={{ gridColumn: '1 / span 2', fontSize: 12, color: 'var(--ink-3)' }}>
            Uma vez por dia, no máximo.
          </div>
        </div>
      )}
    </div>
  );
}

function AguardandoAtivacao() {
  return (
    <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)', marginTop: 6 }}>
      aguardando a ativação
    </div>
  );
}

function SimpleCard({ icon, title, desc, enabled: escolhido, onToggle, recebendo = true, onPedirAtivacao }: {
  icon: string; title: string; desc: string;
  enabled: boolean; onToggle: (v: boolean) => void;
  recebendo?: boolean;
  onPedirAtivacao?: () => void;
}) {
  const enabled = escolhido && recebendo;
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: enabled ? 'var(--ember-soft)' : 'var(--paper-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={icon} size={20} color={enabled ? 'var(--ember)' : 'var(--ink-3)'}/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="serif" style={{ fontSize: 19, lineHeight: 1.1 }}>{title}</div>
            <Toggle on={enabled} onChange={v => (recebendo ? onToggle(v) : onPedirAtivacao && onPedirAtivacao())}/>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{desc}</div>
          {!recebendo && <AguardandoAtivacao />}
        </div>
      </div>
    </div>
  );
}

// Horário em 24 h, de meia em meia hora, só dentro da janela em que um push
// pode sair (8h–21h30). O <input type="time"> mostrava 12 h (AM/PM) e deixava
// escolher 23h, horário em que nada chega (pente fino, UX-13).
const HORARIOS = Array.from({ length: 28 }, (_, i) => {
  const m = 8 * 60 + i * 30;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
});
function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const foraDaJanela = !!value && !HORARIOS.includes(value);
  return (
    <div className="field">
      <label>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}>
        {foraDaJanela && <option value={value}>{value} · fora do horário dos avisos</option>}
        {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ProfileScreen (mantido sem mudanças)
   ───────────────────────────────────────────────────────────── */

export const ProfileScreen: React.FC<{
  profile: ProfileV2 | null;
  streak: { current: number; longest: number } | null;
  finishedBooks: number;
  onOpenPaywall: () => void;
  onOpenReport: () => void;
  onOpenNotif: () => void;
  onOpenTutorial: () => void;
  onOpenTutoriais?: () => void;
  onLogout: () => void;
  onChangePalette: (p: 'midnight' | 'ember' | 'vellum') => void;
  onOpenAdmin?: () => void;
  /** Acesso à biblioteca pessoal — antes era aba; agora é item do perfil. */
  onOpenLibrary?: () => void;
  onOpenReadings?: () => void;
  onOpenStats?: () => void;
  /** Termo escolhido pra clube/grupo (afeta nav e telas) */
  clubsTerm?: 'clube' | 'grupo';
  onChangeClubsTerm?: (t: 'clube' | 'grupo') => void;
  /** Apple Guideline 5.1.1.v — exclusão definitiva da conta. Lança erro pra UI tratar. */
  onDeleteAccount?: () => Promise<void>;
  /** Apple Guideline 3.1.1 — esconde TODA menção a planos pagos no iOS nativo. */
  hidePaywall?: boolean;
  /** Quando vem da aba perfil social como sub-tela \"Ajustes\": mostra voltar. */
  onBack?: () => void;
}> = ({ profile, streak, finishedBooks, onOpenPaywall, onOpenReport, onOpenNotif, onOpenTutorial, onOpenTutoriais, onLogout, onChangePalette, onOpenAdmin, onOpenLibrary, onOpenReadings, onOpenStats, clubsTerm = 'clube', onChangeClubsTerm, onDeleteAccount, hidePaywall = false, onBack }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const displayName = profile?.display_name || 'Leitor';
  const initial = (displayName[0] || 'L').toUpperCase();
  const isPro = !!profile?.plan && profile.plan !== 'free';
  const isAdmin = profile?.role === 'admin';
  const currentPalette = profile?.palette || 'midnight';
  const [showPalettes, setShowPalettes] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const tapBufRef = useRef<{ count: number; first: number }>({ count: 0, first: 0 });
  const handleSecretTap = () => {
    const now = Date.now();
    const buf = tapBufRef.current;
    if (now - buf.first > 4000) { buf.count = 1; buf.first = now; }
    else { buf.count += 1; }
    if (buf.count >= 7) { buf.count = 0; setDebugOpen(true); }
  };
  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      <div style={{ padding: '40px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        {onBack && <button onClick={onBack} aria-label="Voltar" style={{ width: 38, height: 38, borderRadius: 10, border: '1px solid var(--line)', background: 'var(--paper-2)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Icon name="chevron-left" size={18}/></button>}
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{onBack ? 'Ajustes' : 'Perfil'}</div>
      </div>

      <div style={{ padding: '28px 20px 0', textAlign: 'center' }}>
        <div onClick={handleSecretTap} style={{
          width: 86, height: 86, borderRadius: '50%', overflow: 'hidden',
          background: (profile as any)?.avatar_url ? '#1a1512' : 'linear-gradient(135deg, var(--ember), var(--plum))',
          margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--paper)', fontFamily: 'Instrument Serif, serif',
          fontSize: 40, fontStyle: 'italic', cursor: 'pointer', userSelect: 'none',
        }}>{(profile as any)?.avatar_url ? <img src={(profile as any).avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : initial}</div>
        <div className="serif" style={{ fontSize: 28, marginTop: 14, lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {displayName}
          {isPro && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 8px',
              background: 'linear-gradient(135deg, var(--ember), #E87A3B)',
              color: 'var(--paper)', borderRadius: 999,
              fontFamily: 'Geist Mono, monospace',
              fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600,
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z"/></svg>
              {PLAN_NAMES[(profile?.plan as Plan) || 'reader']}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
          {profile?.email}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {streak && streak.current > 0 && (
            <span className="chip"><Flame count={streak.current} size={14}/> {streak.current} {streak.current === 1 ? 'dia seguido' : 'dias seguidos'}</span>
          )}
          {finishedBooks > 0 && <span className="chip">{finishedBooks} livros</span>}
        </div>
      </div>

      {!isPro && (
        <div style={{ padding: '28px 20px 0' }}>
          <button onClick={onOpenPaywall} style={{
            width: '100%', textAlign: 'left',
            padding: '18px', borderRadius: 22,
            background: '#1A1614', color: '#F5F1EA',
            display: 'flex', alignItems: 'center', gap: 12,
            position: 'relative', overflow: 'hidden',
            border: 'none', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <div style={{
              position: 'absolute', top: -30, right: -30,
              width: 140, height: 140, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(199,83,26,0.55), transparent 70%)',
              filter: 'blur(24px)', pointerEvents: 'none',
            }}/>
            <div style={{
              width: 42, height: 42, borderRadius: '50%',
              background: 'var(--ember)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, position: 'relative',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#F5F1EA"><path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z"/></svg>
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              <div className="mono" style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ember)' }}>
                Forjamente · planos
              </div>
              <div className="serif" style={{ fontSize: 18, lineHeight: 1.15, marginTop: 2 }}>Escolha seu ritmo</div>
              <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 4 }}>Leitura e clubes sem limite</div>
            </div>
            <Icon name="chevron-right" size={18} color="#F5F1EA"/>
          </button>
        </div>
      )}

      <div className="section-head"><div className="serif title">Ajustes</div></div>

      <div style={{ padding: '0 20px' }}>
        <div style={{
          background: 'var(--paper-2)', borderRadius: 18, overflow: 'hidden',
          border: '1px solid var(--line)',
        }}>
          {[
            ...(onOpenLibrary ? [{ label: 'Minha estante', icon: 'library', onClick: onOpenLibrary, detail: `${finishedBooks > 0 ? finishedBooks + ' livros' : 'biblioteca'}` }] : []),
            ...(onOpenReadings ? [{ label: 'Minhas leituras', icon: 'check', onClick: onOpenReadings, detail: 'editar · excluir' }] : []),
            ...(onOpenStats ? [{ label: 'Meu ritmo', icon: 'chart', onClick: onOpenStats, detail: 'streak · estatísticas' }] : []),
            // Plano e cobrança — entry permanente pra ver/comparar/trocar plano.
            // Em iOS nativo, o handler abrira /planos no Safari externo via
            // magic link (Apple 3.1.1 — checkout fora do app).
            {
              label: 'Plano e cobrança',
              icon: 'sparkle',
              onClick: onOpenPaywall,
              // "Free" em destaque parecia cobrança para quem está no teste
              // grátis (pente fino, UX-10). Quem não paga mas tem acesso vê
              // até quando; quem não tem, "Sem assinatura".
              detail: (profile?.plan && profile.plan !== 'free')
                ? PLAN_NAMES[profile.plan as Plan]
                : (rotuloDoAcesso(acessoSemAssinatura(profile)) || 'Sem assinatura'),
              accent: true,
            },
            { label: 'Relatório & Insights (IA)', icon: 'sparkle', onClick: onOpenReport, accent: true },
            { label: 'Hábito de notificações',    icon: 'bell',    onClick: onOpenNotif },
            { label: 'Tutoriais',                  icon: 'sparkle', onClick: onOpenTutoriais || onOpenTutorial },
            { label: 'Tema',                       icon: 'moon',    onClick: () => setShowPalettes(s => !s), detail: currentPalette },
            ...(onChangeClubsTerm ? [{ label: 'Termo da comunidade', icon: 'users', onClick: () => onChangeClubsTerm(clubsTerm === 'clube' ? 'grupo' : 'clube'), detail: clubsTerm === 'clube' ? 'Clube' : 'Grupo' }] : []),
            // Documentos legais — abre página standalone em nova aba pra preservar
            // estado do app. Apple e Play exigem acesso fácil a esses textos.
            { label: 'Termos de uso',         icon: 'sparkle', onClick: () => window.open('/termos', '_blank', 'noopener,noreferrer') },
            { label: 'Política de privacidade', icon: 'lock',  onClick: () => window.open('/privacidade', '_blank', 'noopener,noreferrer') },
            // Console admin é desktop-only e fica fora do app, acessado via URL
            // direta forjamente.com.br/admin (sem entry-point por aqui)
          ].map((it: any, i, arr) => (
            <button key={it.label} onClick={it.onClick} style={{
              width: '100%', padding: '14px 16px',
              display: 'flex', alignItems: 'center', gap: 12,
              borderBottom: i < arr.length - 1 ? '1px solid var(--line)' : 'none',
              background: 'transparent', textAlign: 'left',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: it.accent ? 'var(--ember-soft)' : 'var(--paper-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Icon name={it.icon} size={16} color={it.accent ? 'var(--ember)' : 'currentColor'}/></div>
              <div style={{ flex: 1, fontSize: 14 }}>{it.label}</div>
              {it.detail && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{it.detail}</div>}
              <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
            </button>
          ))}
        </div>

        {showPalettes && (
          <div style={{
            marginTop: 10, padding: 14, borderRadius: 14,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            display: 'flex', gap: 8,
          }}>
            {[
              { id: 'midnight' as const, label: 'Midnight', bg: 'linear-gradient(135deg,#161210,#E87A3B)' },
              { id: 'ember' as const,    label: 'Ember',    bg: 'linear-gradient(135deg,#F5F1EA,#C7531A)' },
              { id: 'vellum' as const,   label: 'Vellum',   bg: 'linear-gradient(135deg,#F2EFE8,#1F4E8C)' },
            ].map(p => (
              <button key={p.id} onClick={() => onChangePalette(p.id)} style={{
                flex: 1, height: 60, borderRadius: 10, position: 'relative',
                background: p.bg,
                outline: currentPalette === p.id ? '2px solid var(--ember)' : 'none',
                outlineOffset: 2,
              }}>
                <span style={{
                  position: 'absolute', bottom: 6, left: 8,
                  color: '#fff', fontFamily: 'Geist Mono, monospace',
                  fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                }}>{p.label}</span>
              </button>
            ))}
          </div>
        )}

        <button onClick={onLogout} style={{
          width: '100%', marginTop: 14, padding: '14px 16px',
          background: 'transparent', border: '1px solid var(--line)',
          borderRadius: 18, display: 'flex', alignItems: 'center', gap: 12,
          color: 'var(--ink-2)', textAlign: 'left', fontSize: 14,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'var(--paper-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon name="log-out" size={16}/></div>
          <div style={{ flex: 1 }}>Sair da conta</div>
        </button>

        {onDeleteAccount && (
          <button onClick={() => setDeleteOpen(true)} style={{
            width: '100%', marginTop: 10, padding: '14px 16px',
            background: 'transparent', border: '1px solid rgba(192,58,42,0.35)',
            borderRadius: 18, display: 'flex', alignItems: 'center', gap: 12,
            color: '#C03A2A', textAlign: 'left', fontSize: 14,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(192,58,42,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Icon name="close" size={16} color="#C03A2A"/></div>
            <div style={{ flex: 1 }}>Excluir conta</div>
          </button>
        )}
      </div>

      {deleteOpen && onDeleteAccount && (
        <DeleteAccountSheet
          email={profile?.email}
          onClose={() => setDeleteOpen(false)}
          onConfirm={onDeleteAccount}
        />
      )}
      <DebugSheet
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        profile={profile ? { id: profile.id, email: profile.email, plan: profile.plan, role: profile.role, display_name: profile.display_name } : null}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   DeleteAccountSheet — confirmação dupla pra excluir conta.
   Etapa 1: aviso do que será apagado.
   Etapa 2: digitar EXCLUIR pra liberar o botão.
   ───────────────────────────────────────────────────────────── */
export function DeleteAccountSheet({ email, onClose, onConfirm }: {
  email?: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canDelete = typed.trim().toUpperCase() === 'EXCLUIR' && !busy;

  const handleConfirm = async () => {
    if (!canDelete) return;
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      // sucesso: o caller (App.tsx) faz logout + toast — esse sheet some junto.
    } catch (e: any) {
      setErr(e?.message || 'Não foi possível excluir a conta. Tente de novo.');
      setBusy(false);
    }
  };

  return (
    <div
      onClick={busy ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,16,14,0.55)',
        zIndex: 800, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: 'var(--paper)', borderRadius: '22px 22px 0 0',
          padding: '24px 22px 28px', boxShadow: '0 -10px 40px -10px rgba(0,0,0,0.25)',
          maxHeight: '90vh', overflowY: 'auto',
          animation: 'sheet-up 220ms ease-out',
        }}
      >
        <div style={{
          width: 44, height: 4, borderRadius: 2, background: 'var(--line-strong)',
          margin: '0 auto 16px',
        }}/>

        {step === 1 ? (
          <>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(192,58,42,0.08)', color: '#C03A2A',
              fontFamily: 'Geist Mono, monospace', fontSize: 9,
              letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>
              <Icon name="close" size={11} color="#C03A2A"/> Ação definitiva
            </div>
            <div className="serif" style={{ fontSize: 26, lineHeight: 1.1, marginTop: 12 }}>
              Excluir <span style={{ fontStyle: 'italic', color: '#C03A2A' }}>conta</span>?
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 12, lineHeight: 1.5 }}>
              Tudo associado a <strong>{email || 'esta conta'}</strong> será apagado permanentemente:
            </div>
            <ul style={{
              fontSize: 13, color: 'var(--ink-2)', marginTop: 10, paddingLeft: 20,
              lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <li>livros, sessões de leitura e estatísticas</li>
              <li>conquistas, metas e histórico</li>
              <li>posts, comentários, reações e enquetes em clubes</li>
              <li>clubes que você criou (com todos os membros e conteúdo)</li>
              <li>assinaturas e dados de cobrança</li>
            </ul>
            <div style={{
              marginTop: 14, padding: '10px 12px', borderRadius: 10,
              background: 'rgba(192,58,42,0.06)', color: '#9A2E22',
              fontSize: 12, lineHeight: 1.4,
            }}>
              Não há como recuperar essa conta depois. Cobranças recorrentes ativas devem ser
              canceladas separadamente no app de pagamento.
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button onClick={onClose} style={{
                flex: 1, padding: '14px 16px', borderRadius: 14,
                background: 'var(--paper-3)', color: 'var(--ink)', fontSize: 14,
              }}>Cancelar</button>
              <button onClick={() => setStep(2)} style={{
                flex: 1, padding: '14px 16px', borderRadius: 14,
                background: '#C03A2A', color: '#F5F1EA', fontSize: 14, fontWeight: 500,
              }}>Continuar</button>
            </div>
          </>
        ) : (
          <>
            <div className="serif" style={{ fontSize: 26, lineHeight: 1.1 }}>
              Confirme digitando <span style={{ fontStyle: 'italic', color: '#C03A2A' }}>EXCLUIR</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
              Pra evitar exclusões acidentais, escreva a palavra abaixo. Sua sessão será encerrada
              imediatamente após a exclusão.
            </div>
            <input
              autoFocus
              type="text"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder="EXCLUIR"
              disabled={busy}
              style={{
                width: '100%', marginTop: 14, padding: '14px 16px',
                borderRadius: 12, border: '1px solid var(--line-strong)',
                background: 'var(--paper-2)', color: 'var(--ink)',
                fontSize: 16, fontFamily: 'Geist Mono, monospace',
                letterSpacing: '0.1em', textAlign: 'center',
              }}
            />
            {err && (
              <div style={{
                marginTop: 12, padding: '10px 12px', borderRadius: 10,
                background: 'rgba(192,58,42,0.08)', color: '#C03A2A',
                fontSize: 12.5, lineHeight: 1.4,
              }}>{err}</div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={() => { setStep(1); setTyped(''); setErr(null); }} disabled={busy} style={{
                flex: 1, padding: '14px 16px', borderRadius: 14,
                background: 'var(--paper-3)', color: 'var(--ink)', fontSize: 14,
                opacity: busy ? 0.5 : 1,
              }}>Voltar</button>
              <button onClick={handleConfirm} disabled={!canDelete} style={{
                flex: 1, padding: '14px 16px', borderRadius: 14,
                background: canDelete ? '#C03A2A' : 'rgba(192,58,42,0.35)',
                color: '#F5F1EA', fontSize: 14, fontWeight: 500,
                cursor: canDelete ? 'pointer' : 'not-allowed',
              }}>{busy ? 'Excluindo…' : 'Excluir definitivamente'}</button>
            </div>
          </>
        )}

        <style>{`@keyframes sheet-up { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
      </div>
    </div>
  );
}
// Card de diagnostico - mostra versao do IPA + permissoes push detalhadas.
// Botao "Re-checar" atualiza estado em tempo real.
// Botao "Forcar registro push" roda registerNativePush e mostra resultado de
// cada step (request permission, register APNs, get FCM token, POST backend).
export function DiagnosticCard({ onActivatePush, isAdmin }: { onActivatePush?: () => void | Promise<void>; isAdmin?: boolean } = {}) {
  // So mostra DiagnosticCard se:
  //   1. User eh admin (profile.role === 'admin' verificado em App.tsx)
  //   2. Debug mode ativo (?debug=1 na URL OU localStorage.forjamente_debug='1')
  // Pra ativar:
  //   - URL: https://forjamente.com.br/?debug=1
  //   - Console: localStorage.setItem('forjamente_debug', '1') + reload
  // Pra desativar: localStorage.removeItem('forjamente_debug')

  const [info, setInfo] = React.useState<any>(null);
  const [vw, setVw] = React.useState<any>(null);
  const [pushSteps, setPushSteps] = React.useState<any[]>([]);
  const [running, setRunning] = React.useState(false);

  // Escuta eventos de progresso disparados por registerNativePush em platform.ts
  React.useEffect(() => {
    const handler = (e: any) => {
      const { label, ok, detail } = e.detail || {};
      setPushSteps(s => [...s, { label, ok, detail }]);
    };
    window.addEventListener('forjamente:push-step', handler);
    return () => window.removeEventListener('forjamente:push-step', handler);
  }, []);

  const runDiagnostic = React.useCallback(async () => {
    const buildVersion = document.querySelector('meta[name="build-version"]')?.getAttribute('content') || 'N/A';

    setVw({
      w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio,
      sw: (window as any).screen?.width || 0, sh: (window as any).screen?.height || 0,
      contentInset: getComputedStyle(document.querySelector('.app-shell') || document.body).paddingTop,
      bodyFontSize: getComputedStyle(document.body).fontSize,
    });

    try {
      const cap = (window as any).Capacitor;
      if (cap?.isNativePlatform?.()) {
        const appMod: any = await import('@capacitor/app');
        const appInfo = await appMod.App.getInfo();

        let pushPerm: any = { error: 'plugin not loaded' };
        try {
          const pnMod: any = await import('@capacitor/push-notifications');
          const PN = pnMod.PushNotifications;
          if (PN?.checkPermissions) pushPerm = await PN.checkPermissions();
        } catch (e: any) { pushPerm = { error: e?.message || String(e) }; }

        let localPerm: any = { error: 'plugin not loaded' };
        try {
          const lnMod: any = await import('@capacitor/local-notifications');
          const LN = lnMod.LocalNotifications;
          if (LN?.checkPermissions) localPerm = await LN.checkPermissions();
        } catch (e: any) { localPerm = { error: e?.message || String(e) }; }

        setInfo({
          version: appInfo.version, build: appInfo.build, buildVersion,
          isNative: true, pushPerm, localPerm
        });
      } else {
        setInfo({ buildVersion, isNative: false });
      }
    } catch (e: any) {
      setInfo({ buildVersion: 'N/A', error: e?.message || 'unknown' });
    }
  }, []);

  React.useEffect(() => { runDiagnostic(); }, [runDiagnostic]);

  const forceRegister = React.useCallback(async () => {
    setRunning(true);
    setPushSteps([]);

    if (!onActivatePush) {
      setPushSteps([{ label: 'onActivatePush nao fornecido', ok: false, detail: 'prop missing' }]);
      setRunning(false);
      return;
    }

    try {
      // Chama o flow completo de App.tsx que:
      // 1. Verifica permission (request se needed)
      // 2. Chama PushNotifications.register()
      // 3. Aguarda token via listener
      // 4. POST pra /api/push/fcm-register (CRITICO — atualiza backend)
      // Cada step dispatcha forjamente:push-step que e capturado pelo
      // useEffect listener acima e adicionado em pushSteps automaticamente.
      await onActivatePush();
      runDiagnostic();
    } catch (e: any) {
      setPushSteps(s => [...s, { label: 'Exception geral', ok: false, detail: e?.message || String(e) }]);
    } finally {
      setRunning(false);
    }
  }, [onActivatePush, runDiagnostic]);

  // Early return DEPOIS de todos os hooks (rule of hooks)
  const debugMode = (typeof window !== 'undefined') && (
    window.location.search.includes('debug=1') ||
    window.localStorage.getItem('forjamente_debug') === '1'
  );
  if (!isAdmin || !debugMode) return null;

  return (
    <div style={{
      margin: '20px 0', padding: 14,
      background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14,
      fontSize: 11, fontFamily: 'monospace', color: 'var(--ink-3)', lineHeight: 1.6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, color: 'var(--ember)' }}>Diagnostico</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={runDiagnostic} style={{
            fontSize: 10, padding: '4px 10px', borderRadius: 8,
            background: 'var(--paper-3)', color: 'var(--ink-2)', border: '1px solid var(--line)',
          }}>Re-checar</button>
          <button onClick={forceRegister} disabled={running} style={{
            fontSize: 10, padding: '4px 10px', borderRadius: 8,
            background: running ? 'var(--paper-3)' : 'var(--ember)',
            color: running ? 'var(--ink-3)' : 'var(--paper)',
            border: '1px solid var(--ember)', opacity: running ? 0.6 : 1,
          }}>{running ? 'Rodando...' : 'Forcar registro push'}</button>
        </div>
      </div>

      {info && (
        <>
          <div>IPA: {info.version || '-'} ({info.build || '-'})</div>
          <div>Site build: {info.buildVersion}</div>
          <div>Native: {info.isNative ? 'sim' : 'nao'}</div>
          {info.pushPerm && (
            <div style={{ marginTop: 4 }}>
              Push perm: <span style={{ color: info.pushPerm.receive === 'granted' ? '#9EB083' : 'var(--ember)' }}>{JSON.stringify(info.pushPerm)}</span>
            </div>
          )}
          {info.localPerm && (
            <div>Local perm: <span style={{ color: info.localPerm.display === 'granted' ? '#9EB083' : 'var(--ember)' }}>{JSON.stringify(info.localPerm)}</span></div>
          )}
          {info.error && <div style={{ color: 'var(--ember)' }}>Erro: {info.error}</div>}
        </>
      )}
      {vw && (
        <div style={{ marginTop: 8 }}>
          <div>vw: {vw.w}x{vw.h} dpr:{vw.dpr}</div>
          <div>screen: {vw.sw}x{vw.sh}</div>
          <div>body font: {vw.bodyFontSize}</div>
          <div>app-shell pad-top: {vw.contentInset}</div>
        </div>
      )}

      {pushSteps.length > 0 && (
        <div style={{ marginTop: 12, padding: 10, background: 'var(--paper-3)', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: 'var(--ember)', marginBottom: 6 }}>Push register steps</div>
          {pushSteps.map((s, i) => (
            <div key={i} style={{ marginBottom: 3 }}>
              <span style={{
                color: s.ok === true ? '#9EB083' : s.ok === false ? 'var(--ember)' : 'var(--ink-3)',
                fontWeight: 600, marginRight: 6,
              }}>{s.ok === true ? '[OK]' : s.ok === false ? '[ERR]' : '[..]'}</span>
              {s.label}{s.detail ? ': ' + s.detail : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

